import React from "react";
import "style/MsTrader.scss";

import { Link } from "react-router-dom";

import landing from "config/i18n/landing";
import ui from "config/i18n/ui";
import useLang from "hooks/useLang";
import usePageTitle from "hooks/usePageTitle";

import Reveal from "components/Reveal";

import MsTraderDesk from "assets/image/MsTraderDesk.webp";
import MsTraderChart from "assets/image/MsTraderChart.webp";
import MsTraderMarket from "assets/image/MsTraderMarket.webp";
import MsTraderOrders from "assets/image/MsTraderOrders.webp";
import MsTraderOtc from "assets/image/MsTraderOtc.webp";

// Index-matched to landing.mstrader.showcase.items
const SHOTS = [MsTraderChart, MsTraderMarket, MsTraderOrders, MsTraderOtc];

// Narrow panel crops are shown at natural size inside the frame
const FIT_SHOTS = new Set([3]);

const MsTrader = () => {
  const lang = useLang();
  const t = landing[lang].mstrader;
  usePageTitle(t.pageTitle);

  return (
    <div className="lp lp--mstrader">
      <header className="lp-hero">
        <div className="lp-hero__grid" aria-hidden="true" />
        <div className="lp-hero__overlay" aria-hidden="true" />

        <div className="shell lp-hero__inner">
          <div className="lp-hero__copy">
            <span className="lp-hero__eyebrow">{t.eyebrow}</span>
            <h1 className="lp-hero__title">
              {t.title[0]}
              <br />
              <span className="text-gradient">{t.title[1]}</span>
            </h1>
            <p className="lp-hero__lead">{t.lead}</p>
            <div className="lp-hero__actions">
              <Link className="btn btn--primary" to="/ms-trader/architecture">
                {t.primary}
              </Link>
              <Link className="btn btn--ghost" to="/contact">
                {t.secondary}
              </Link>
            </div>
          </div>

          <div className="lp-hero__visual">
            <div className="lp-frame">
              <img src={MsTraderDesk} alt={ui[lang].deskAlt} />
            </div>
          </div>
        </div>

        <div className="shell">
          <ul className="lp-stats">
            {t.stats.map((stat) => (
              <li key={stat.value}>
                <span className="lp-stats__value">{stat.value}</span>
                <span className="lp-stats__label">{stat.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </header>

      <main>
        <section className="lp-section lp-section--light">
          <div className="shell">
            <Reveal className="section-head">
              <h2>
                {t.showcase.title[0]}{" "}
                <span className="accent">{t.showcase.title[1]}</span>
              </h2>
            </Reveal>

            <div className="lp-showcase">
              {t.showcase.items.map((item, index) => (
                <Reveal key={item.title} className="lp-showcase__item">
                  <div
                    className={`lp-frame lp-frame--light${
                      FIT_SHOTS.has(index) ? " lp-frame--fit" : ""
                    }`}
                  >
                    <img src={SHOTS[index]} alt={item.title} loading="lazy" />
                  </div>
                  <div className="lp-showcase__copy">
                    <span className="lp-card__index">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section lp-section--muted">
          <div className="shell">
            <Reveal className="section-head">
              <h2>
                {t.more.title[0]}{" "}
                <span className="accent">{t.more.title[1]}</span>
              </h2>
            </Reveal>

            <div className="lp-grid">
              {t.more.items.map((item, index) => (
                <Reveal key={item.title} className="lp-cell" delay={index * 60}>
                  <article className="lp-card">
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal className="lp-chipset">
              <h3>{t.orderTypes.title}</h3>
              <div className="lp-chips">
                {t.orderTypes.chips.map((chip) => (
                  <span className="chip" key={chip}>
                    {chip}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="lp-section lp-section--dark">
          <div className="shell">
            <Reveal className="section-head">
              <h2>
                {t.ops.title[0]}{" "}
                <span className="accent">{t.ops.title[1]}</span>
              </h2>
            </Reveal>

            <div className="lp-grid lp-grid--4">
              {t.ops.items.map((item, index) => (
                <Reveal key={item.title} className="lp-cell" delay={index * 50}>
                  <article className="lp-card">
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section lp-section--light">
          <div className="shell">
            <Reveal className="lp-cta">
              <div>
                <h2>{t.cta.title}</h2>
                <p>{t.cta.text}</p>
              </div>
              <div className="lp-cta__actions">
                <Link className="btn btn--primary" to="/contact">
                  {t.cta.button}
                </Link>
                <Link className="btn btn--ghost" to="/ms-trader/architecture">
                  {t.cta.secondary}
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </div>
  );
};

export default MsTrader;
